/* biome-ignore-all lint/style/useFilenamingConvention: TanStack Router requires $param syntax for dynamic routes */
import { Render, resolveAllData } from "@measured/puck";
import "@measured/puck/puck.css";
import { Button } from "@designs-by-maryam/ui/components/button";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Loader2 } from "lucide-react";
import { type PuckPageData, puckConfig } from "@/lib/puck/config";
import { pageBySlugQueryOptions } from "@/lib/query-options";

export const Route = createFileRoute("/p/$slug")({
	loader: async ({ params, context }) => {
		// SSR: Use per-request queryClient from context to prevent cache bleeding
		const { queryClient } = context;
		const page = await queryClient.ensureQueryData(
			pageBySlugQueryOptions(params.slug),
		);

		// Resolve all Puck component data server-side to prevent hydration mismatches
		// This executes all resolveData functions in the config before rendering
		let resolvedData = page?.puck_data as PuckPageData | null;
		if (page?.puck_data) {
			try {
				resolvedData = await resolveAllData(
					page.puck_data as PuckPageData,
					puckConfig,
				);
			} catch {
				// Fall back to unresolved data if resolution fails
				resolvedData = page.puck_data as PuckPageData;
			}
		}

		return { slug: params.slug, page, resolvedData };
	},
	head: ({ loaderData }) => ({
		meta: [
			{
				title: loaderData?.page?.title
					? `${loaderData.page.title} | Designs by Maryam`
					: "Designs by Maryam",
			},
			{
				name: "description",
				content:
					loaderData?.page?.meta_description ||
					"Custom interior design pages by Designs by Maryam",
			},
		],
	}),
	component: PuckPage,
});

/**
 * Public Page Renderer
 *
 * Renders published Puck pages by slug.
 * Only published pages are accessible via this route.
 * Uses SSR prefetch for instant page rendering.
 */
function PuckPage() {
	const { slug } = Route.useParams();
	const loaderData = Route.useLoaderData();

	const {
		data: page,
		isLoading,
		error,
	} = useQuery({
		...pageBySlugQueryOptions(slug),
		staleTime: 5 * 60 * 1000, // 5 minutes - override for page-specific caching
	});

	// Use SSR-resolved data from loader, or fall back to client data
	const resolvedData =
		loaderData?.resolvedData ?? (page?.puck_data as PuckPageData);

	// Loading state (only on cache miss)
	if (isLoading) {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<Loader2 className="h-8 w-8 animate-spin text-primary" />
			</div>
		);
	}

	// Error state
	if (error) {
		return (
			<div className="flex min-h-screen flex-col items-center justify-center gap-4">
				<h1 className="font-bold text-2xl text-destructive">
					Error loading page
				</h1>
				<p className="text-muted-foreground">{error.message}</p>
				<Button asChild variant="outline">
					<Link to="/">
						<ArrowLeft className="mr-2 h-4 w-4" />
						Back to Home
					</Link>
				</Button>
			</div>
		);
	}

	// Not found state
	if (!page) {
		return (
			<div className="flex min-h-screen flex-col items-center justify-center gap-4">
				<h1 className="font-bold text-4xl">404</h1>
				<p className="text-muted-foreground">Page not found</p>
				<Button asChild variant="outline">
					<Link to="/">
						<ArrowLeft className="mr-2 h-4 w-4" />
						Back to Home
					</Link>
				</Button>
			</div>
		);
	}

	return <Render config={puckConfig} data={resolvedData} />;
}
